import { Localization } from "@viamap/viamap2-common";
import {
   historyCommentForProvenance,
   type ANNServitutProvenance,
} from "src/modules/ANNModule/ANNServitutProvenance";
import {
   saveUniqueFieldNote,
   type ServitutAnalysisFieldKey,
} from "src/modules/ANNModule/ANNServitutFieldAnnotations";
import {
   AnnotationContextType,
   AnnotationType,
   type ANNAnnotationRecord,
   type SaveAnnotationInput,
} from "src/modules/ANNModule/ANNTypes";
import { SERVITUT_IGNORE_NOTE_COMMENT } from "../model/ignore";
import { rowDocumentId } from "../model/identity";
import { mergeAnalysisItems } from "../model/projectAnn";
import { normalizeAktPages } from "../model/pages";
import { isDigitalRow, type ServitutRow } from "../model/row";

export type CommitSession = {
   organizationId: string;
   userId: string;
   userName: string;
};

export function provenanceFromRow(row: ServitutRow): ANNServitutProvenance {
   return {
      reviewState: row.review.state,
      origin: row.review.origin,
      source: row.document.kind === "upload"
         || (row.document.kind === "annotation" && row.document.origin === "upload")
         ? "file"
         : "akt",
      aiPagesKept: row.pages.aiSuggested,
      analysisStale: Boolean(row.analysis?.stale),
      fieldStatus: {
         pages: row.review.state === "checked" ? "person_approved" : undefined,
         resume: row.fields.resume.aiStatus,
         entitledParty: row.fields.entitledParty.aiStatus,
         condition: row.fields.condition.aiStatus,
         statement: row.fields.statement.aiStatus,
      },
   };
}

export function buildPageDocumentSaveInput(
   row: ServitutRow,
   session: CommitSession,
   document: {
      s3Key: string;
      filename?: string;
      s3Url?: string;
      mimeType?: string;
      size?: number;
      existing?: ANNAnnotationRecord;
   },
): SaveAnnotationInput {
   const pages = normalizeAktPages(row.pages.selected.length ? row.pages.selected : row.pages.aiSuggested);
   const provenance = provenanceFromRow(row);
   const existing = document.existing;
   const liveAnalysis = row.analysis
      ? {
         analyzedAt: new Date().toISOString(),
         caseDescription: row.analysis.item.case_description ?? existing?.document?.llmAnalysis?.caseDescription ?? null,
         item: mergeAnalysisItems(row.analysis.item, existing?.document?.llmAnalysis?.item) || row.analysis.item,
      }
      : existing?.document?.llmAnalysis;
   return {
      type: AnnotationType.Document,
      documentId: existing?.documentId || rowDocumentId(row),
      contextType: AnnotationContextType.Servitut,
      organizationId: session.organizationId,
      userId: session.userId,
      userName: session.userName,
      datoloebenr: existing?.datoloebenr || row.identity.datoloebenr,
      document: {
         filename: document.filename,
         s3Key: document.s3Key,
         s3Url: document.s3Url,
         mimeType: document.mimeType || "application/pdf",
         size: document.size,
         llmAnalysis: liveAnalysis,
         provenance,
         ...(pages.length && row.pages.extent !== "entire_akt" ? { extractedPages: pages } : {}),
      },
      title: (document.filename || existing?.title || row.landRegistry.displayAktId || row.id).replace(/\.pdf$/i, ""),
      historyComment: [
         pages.length
            ? Localization.getFormattedText("Saved from servitutredegørelse. Pages: {pages}", { pages: pages.join(", ") })
            : Localization.getText("Saved from servitutredegørelse."),
         historyCommentForProvenance(provenance),
      ].filter(Boolean).join(" · "),
      verified: true,
      annotationType: existing?.annotationType || "Dokument",
      id: existing?.id,
      originalCreatedAt: existing?.createdAt,
      originalCreatedBy: existing?.createdBy,
      originalCreatedByName: existing?.createdByName,
   };
}

export function canCommitPagesWithoutPdfFetch(row: ServitutRow, existing?: ANNAnnotationRecord): boolean {
   if (row.document.kind === "annotation" && Boolean(row.document.s3Key)) {
      return true;
   }
   return isDigitalRow(row) && row.document.kind !== "upload" && Boolean(existing?.document?.s3Key);
}

export function buildIgnoreNoteSaveInput(
   row: ServitutRow,
   session: CommitSession,
   existing?: ANNAnnotationRecord,
): SaveAnnotationInput {
   return {
      type: AnnotationType.Note,
      documentId: existing?.documentId || rowDocumentId(row),
      contextType: AnnotationContextType.Servitut,
      organizationId: session.organizationId,
      userId: session.userId,
      userName: session.userName,
      datoloebenr: existing?.datoloebenr || row.identity.datoloebenr,
      note: { comment: SERVITUT_IGNORE_NOTE_COMMENT, text: "ignored" },
      title: Localization.getText("Ignored"),
      historyComment: Localization.getText("Ignored from servitutredegørelse"),
      verified: true,
      annotationType: "Notat",
      id: existing?.id,
      originalCreatedAt: existing?.createdAt,
      originalCreatedBy: existing?.createdBy,
      originalCreatedByName: existing?.createdByName,
   };
}

export async function commitFieldNote(params: {
   row: ServitutRow;
   field: ServitutAnalysisFieldKey;
   text: string;
   session: CommitSession;
   existing?: ANNAnnotationRecord;
   siblings?: ANNAnnotationRecord[];
   saveAnnotation: (input: SaveAnnotationInput) => Promise<ANNAnnotationRecord>;
   deleteAnnotation: (id: string, organizationId?: string) => Promise<void>;
}): Promise<ANNAnnotationRecord> {
   return saveUniqueFieldNote({
      field: params.field,
      documentId: rowDocumentId(params.row),
      datoLoebenr: params.row.identity.datoloebenr,
      organizationId: params.session.organizationId,
      userId: params.session.userId,
      userName: params.session.userName,
      text: params.text,
      existing: params.existing,
      siblings: params.siblings,
      verified: true,
      historyComment: Localization.getText("Accepted from servitutredegørelse"),
      paataleberettigetResolution: params.field === "entitledParty"
         ? params.row.analysis?.item.paataleberettiget_resolution
         : undefined,
      saveAnnotation: params.saveAnnotation,
      deleteAnnotation: params.deleteAnnotation,
   });
}
